import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../utils/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import AccountLayout from '../components/AccountLayout';
import { LuPackage, LuChevronDown } from 'react-icons/lu';

const STATUS_STYLE = {
  Processing: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900/50",
  Shipped:    "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-900/50",
  Delivered:  "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900/50",
  Cancelled:  "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900/50",
};

export default function Orders() {
  const { user }                          = useAuth();
  const [orders, setOrders]               = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'orders'), where('userId', '==', user.uid), orderBy('createdAt', 'desc')));
        setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } finally { setLoading(false); }
    })();
  }, [user]);

  return (
    <AccountLayout title="My Orders">
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="animate-pulse bg-muted rounded-2xl h-20" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border shadow-sm p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
            <LuPackage size={24} className="text-[var(--color-muted-foreground)]" />
          </div>
          <p className="font-bold text-foreground mb-1">No orders yet</p>
          <p className="text-[var(--color-muted-foreground)] text-sm">Your wholesale orders will appear here once placed.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => {
            const statusCls  = STATUS_STYLE[order.status] || "bg-slate-50 text-slate-600 border-slate-200 dark:bg-white/5 dark:text-slate-300 dark:border-white/10";
            const isSelected = selectedOrder?.id === order.id;
            return (
              <div key={order.id} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
                <button
                  className="w-full px-6 py-5 flex flex-wrap items-center justify-between gap-4 text-left hover:bg-muted transition-colors"
                  onClick={() => setSelectedOrder(isSelected ? null : order)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-brand-navy flex items-center justify-center flex-shrink-0">
                      <LuPackage size={18} className="text-brand-amber" />
                    </div>
                    <div>
                      <p className="font-bold text-foreground text-sm">Order #{order.id.slice(-8).toUpperCase()}</p>
                      <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5">{order.createdAt?.toDate().toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${statusCls}`}>{order.status}</span>
                    <span className="font-black text-foreground">${Number(order.total).toFixed(2)}</span>
                    <LuChevronDown size={16} className={`text-[var(--color-muted-foreground)] transition-transform ${isSelected ? "rotate-180" : ""}`} />
                  </div>
                </button>

                {isSelected && (
                  <div className="border-t border-border px-6 py-5 bg-muted dark:bg-[#0E1223]">
                    <p className="text-xs font-bold text-[var(--color-muted-foreground)] uppercase tracking-widest mb-3">Order Items</p>
                    <div className="space-y-2">
                      {order.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-foreground font-medium">{item.name} <span className="text-[var(--color-muted-foreground)]">× {item.quantity}</span></span>
                          <span className="font-bold text-foreground">${Number(item.price).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-border flex justify-between">
                      <span className="text-sm font-bold text-[var(--color-muted-foreground)] uppercase tracking-wider">Total</span>
                      <span className="font-black text-foreground">${Number(order.total).toFixed(2)}</span>
                    </div>
                    {order.shippingAddress && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-xs font-bold text-[var(--color-muted-foreground)] uppercase tracking-widest mb-1">Ship To</p>
                        <p className="text-sm text-foreground">
                          {order.shippingAddress.street}, {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AccountLayout>
  );
}
